import {
	Box,
	Button,
	VStack,
	Text,
	Heading,
	Flex,
	Icon,
	Checkbox,
	Alert,
	AlertIcon,
	AlertDescription,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Portal,
	Badge,
	Collapse,
	useDisclosure,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaCheckCircle, FaExclamationCircle, FaTimesCircle, FaCheck, FaArrowRight } from 'react-icons/fa';
import { StepFooter } from './StepFooter';

export interface PartstraderLocation {
	id: string;
	name: string;
}

export interface CccLocation {
	id: string;
	name: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
}

export interface LocationMapping {
	partstraderId: string;
	cccId: string;
}

/** Per-CCC row state: enabled flag + which PT ids are selected */
export interface CccRowMapping {
	enabled: boolean;
	ptIds: string[];
}

interface LocationMappingUIProps {
	partstraderLocations: PartstraderLocation[];
	cccLocations: CccLocation[];
	suggestedMappings: LocationMapping[];
	onConfirm: (mappings: LocationMapping[]) => void;
	/** Show spinner on confirm button while creating locations */
	isConfirmLoading?: boolean;
	cccMappings: Record<string, CccRowMapping>;
	onToggleCcc: (cccId: string) => void;
	onAddPt: (cccId: string, ptId: string) => void;
	onRemovePt: (cccId: string, ptId: string) => void;
	onBack?: () => void;
	onBackToSetup?: () => void;
	usesPt?: boolean;
}

export const LocationMappingUI = ({
	partstraderLocations,
	cccLocations,
	onConfirm,
	cccMappings,
	onToggleCcc,
	onAddPt,
	onRemovePt,
	isConfirmLoading = false,
	onBack,
	onBackToSetup,
	usesPt = true,
}: LocationMappingUIProps) => {
	const issuesDisclosure = useDisclosure();
	const ptMap = new Map(partstraderLocations.map((pt) => [pt.id, pt]));

	const allAssignedPtIds = new Set(
		Object.values(cccMappings).flatMap((row) => (row.enabled ? row.ptIds : []))
	);
	const unassignedPt = partstraderLocations.filter((pt) => !allAssignedPtIds.has(pt.id));

	const issues: { type: 'unmatched-ccc' | 'unmatched-pt'; label: string; id: string }[] = [];
	if (usesPt) {
		cccLocations.forEach((ccc) => {
			const row = cccMappings[ccc.id];
			if (row?.enabled && row.ptIds.length === 0) {
				issues.push({ type: 'unmatched-ccc', label: ccc.name, id: ccc.id });
			}
		});
		unassignedPt.forEach((pt) => {
			issues.push({ type: 'unmatched-pt', label: pt.name, id: pt.id });
		});
	}

	const enabledCount = cccLocations.filter((ccc) => cccMappings[ccc.id]?.enabled).length;

	const handleConfirm = () => {
		const flat: LocationMapping[] = [];
		for (const ccc of cccLocations) {
			const row = cccMappings[ccc.id];
			if (!row?.enabled) continue;
			if (usesPt) {
				for (const ptId of row.ptIds) {
					flat.push({ partstraderId: ptId, cccId: ccc.id });
				}
			} else {
				flat.push({ partstraderId: '', cccId: ccc.id });
			}
		}
		onConfirm(flat);
	};

	const getRowStatus = (ccc: CccLocation): 'mapped' | 'unmapped' | 'disabled' => {
		const row = cccMappings[ccc.id];
		if (!row?.enabled) return 'disabled';
		if (!usesPt) return 'mapped';
		return row.ptIds.length > 0 ? 'mapped' : 'unmapped';
	};

	return (
		<Flex direction="column" flex={1} width="100%" minH={0}>
			{/* Fixed header area */}
			<Box flexShrink={0} pt={4}>
				<Box mb={4}>
					<Heading size="md" mb={1}>{usesPt ? 'Map Locations' : 'Confirm Locations'}</Heading>
					<Text color="gray.500" fontSize="sm">
						{usesPt
							? 'Match each CCC ONE location to its PartsTrader counterpart. Uncheck to mark as unused.'
							: 'Review your CCC ONE locations below. Uncheck any you don\'t want to include.'}
					</Text>
				</Box>

			{issues.length === 0 ? (
				<Alert status="success" borderRadius="md" border="1px solid" borderColor="green.300" bg="green.50" mb={4}>
					<AlertIcon color="green.500" />
					<AlertDescription color="gray.700" fontSize="sm">
						{usesPt ? 'All locations are mapped.' : `${enabledCount} location${enabledCount !== 1 ? 's' : ''} selected.`}
					</AlertDescription>
				</Alert>
				) : (
					<Box
						mb={4}
						borderWidth={1}
						borderColor="red.300"
						borderRadius="md"
						bg="red.50"
						overflow="hidden"
					>
						<Flex
							px={4}
							py={2.5}
							align="center"
							justify="space-between"
							cursor="pointer"
							onClick={issuesDisclosure.onToggle}
							_hover={{ bg: 'red.100' }}
							transition="background 0.15s"
						>
							<Flex align="center" gap={2}>
								<Icon as={FaExclamationCircle as React.ElementType} color="red.500" boxSize={4} />
								<Text fontSize="sm" fontWeight="semibold" color="gray.700">
									{issues.length} issue{issues.length !== 1 ? 's' : ''} detected
								</Text>
							</Flex>
							<Flex align="center" gap={2}>
								<Badge colorScheme="red" borderRadius="full" px={2} fontSize="xs">{issues.length}</Badge>
								<Icon
									as={(issuesDisclosure.isOpen ? FaChevronUp : FaChevronDown) as React.ElementType}
									color="gray.500"
									boxSize={3}
								/>
							</Flex>
						</Flex>
						<Collapse in={issuesDisclosure.isOpen} animateOpacity>
							<VStack align="stretch" spacing={2} px={4} pb={3} pt={1}>
								{issues.map((issue) => (
									<Alert key={`${issue.type}-${issue.id}`} status="error" borderRadius="md" border="1px solid" borderColor="red.300" bg="white">
										<AlertIcon color="red.500" />
										<AlertDescription color="gray.700" fontSize="sm">
											{issue.type === 'unmatched-ccc'
												? `"${issue.label}" has no PartsTrader location assigned.`
												: `PartsTrader "${issue.label}" is not assigned to any CCC location.`}
										</AlertDescription>
									</Alert>
								))}
							</VStack>
						</Collapse>
					</Box>
				)}
			</Box>

			{/* Scrollable table area */}
			<Flex direction="column" flex={1} minH={0}>
				<Box borderWidth={1} borderColor="gray.200" borderRadius="md" overflow="hidden" display="flex" flexDirection="column" flex={1} minH={0}>
				{/* Sticky table header */}
				<Flex
					px={4} py={2.5} bg="gray.50" borderBottomWidth={1} borderColor="gray.200"
					align="center" flexShrink={0}
				>
					<Box width="36px" flexShrink={0} />
					<Text flex={1} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" minW={0}>
						CCC Name
					</Text>
					{usesPt && (
						<>
							<Box width="32px" flexShrink={0} />
							<Text flex={1} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" minW={0}>
								PartsTrader Mapping
							</Text>
						</>
					)}
					<Text flex={1} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" minW={0}>
						Address
					</Text>
					<Text width="60px" fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" textAlign="center" flexShrink={0}>
						Status
					</Text>
				</Flex>

					{/* Scrollable table body */}
					<Box flex={1} minH={0} overflowY="auto">
						{cccLocations.map((ccc, idx) => {
							const row = cccMappings[ccc.id] ?? { enabled: true, ptIds: [] };
							const status = getRowStatus(ccc);
							const availablePt = partstraderLocations.filter(
								(pt) => row.ptIds.includes(pt.id) || !allAssignedPtIds.has(pt.id)
							);
							const isLast = idx === cccLocations.length - 1;

							const mappingLabel = row.ptIds.length === 0
								? 'Select location'
								: row.ptIds.map((id) => ptMap.get(id)?.name ?? id).join(', ');

							return (
								<Flex
									key={ccc.id} px={4} py={3}
									borderBottomWidth={isLast ? 0 : 1} borderColor="gray.200"
									align="center"
									bg={status === 'disabled' ? 'gray.50' : 'white'}
									opacity={status === 'disabled' ? 0.6 : 1}
									_hover={{ bg: status === 'disabled' ? 'gray.50' : 'gray.25' }}
									transition="background 0.15s"
								>
									{/* Checkbox */}
									<Box width="36px" flexShrink={0}>
										<Checkbox isChecked={row.enabled} onChange={() => onToggleCcc(ccc.id)} colorScheme="blue" size="md" />
									</Box>

									{/* CCC Name */}
									<Box flex={1} minW={0} pr={3}>
										<Text fontSize="sm" fontWeight="medium" noOfLines={1}>{ccc.name}</Text>
									</Box>

								{/* Arrow + PartsTrader Mapping (hidden when PT not used) */}
								{usesPt && (
									<>
										<Flex width="32px" justify="center" flexShrink={0}>
											<Icon as={FaArrowRight as React.ElementType} color={status === 'disabled' ? 'gray.300' : 'gray.400'} boxSize={3} />
										</Flex>

										<Box flex={1} minW={0} pr={3}>
											{row.enabled ? (
												<Menu closeOnSelect={false}>
													<MenuButton
														as={Button} size="xs" variant="outline"
														rightIcon={<Icon as={FaChevronDown as React.ElementType} boxSize={2} />}
														fontWeight="normal"
														color={row.ptIds.length === 0 ? 'red.500' : 'gray.700'}
														borderColor={row.ptIds.length === 0 ? 'red.300' : 'gray.200'}
														_hover={{ borderColor: row.ptIds.length === 0 ? 'red.400' : 'gray.300' }}
														width="100%" maxW="220px"
														textAlign="left"
													>
														<Text noOfLines={1} fontSize="xs">
															{mappingLabel}
														</Text>
													</MenuButton>
													<Portal>
														<MenuList maxH="200px" overflowY="auto" zIndex={1400} fontSize="sm">
															{availablePt.map((pt) => {
																const isSelected = row.ptIds.includes(pt.id);
																return (
																	<MenuItem
																		key={pt.id}
																		onClick={() => isSelected ? onRemovePt(ccc.id, pt.id) : onAddPt(ccc.id, pt.id)}
																		fontSize="sm"
																		bg={isSelected ? 'blue.50' : undefined}
																		_hover={{ bg: isSelected ? 'blue.100' : 'gray.100' }}
																	>
																		<Flex align="center" justify="space-between" width="100%">
																			<Text>{pt.name}</Text>
																			<Box width="16px" flexShrink={0}>
																				{isSelected && <Icon as={FaCheck as React.ElementType} color="blue.500" boxSize={3} />}
																			</Box>
																		</Flex>
																	</MenuItem>
																);
															})}
														</MenuList>
													</Portal>
												</Menu>
											) : (
												<Text fontSize="xs" color="gray.400" fontStyle="italic">Unused</Text>
											)}
										</Box>
									</>
								)}

									{/* Address */}
									<Box flex={1} minW={0} pr={3}>
										{ccc.address ? (
											<>
												<Text fontSize="xs" color="gray.500" noOfLines={1}>{ccc.address}</Text>
												{(ccc.city || ccc.state || ccc.zip) && (
													<Text fontSize="xs" color="gray.400" noOfLines={1}>
														{[ccc.city, [ccc.state, ccc.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
													</Text>
												)}
											</>
										) : (
											<Text fontSize="xs" color="gray.400">—</Text>
										)}
									</Box>

									{/* Status */}
									<Flex width="60px" justify="center" flexShrink={0}>
										{status === 'mapped' && <Icon as={FaCheckCircle as React.ElementType} color="green.500" boxSize={4} />}
										{status === 'unmapped' && <Icon as={FaExclamationCircle as React.ElementType} color="red.500" boxSize={4} />}
										{status === 'disabled' && <Icon as={FaTimesCircle as React.ElementType} color="gray.300" boxSize={4} />}
									</Flex>
								</Flex>
							);
						})}
					</Box>
				</Box>
			</Flex>

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				secondaryActions={onBackToSetup
					? [{ label: 'Back to Setup', onClick: onBackToSetup, variant: 'outline' }]
					: undefined}
				primary={{
					label: usesPt ? 'Confirm Mapping' : 'Confirm Locations',
					onClick: handleConfirm,
					isLoading: isConfirmLoading,
					loadingText: 'Creating locations...',
				}}
			/>
		</Flex>
	);
};
